import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import { usePayment } from '../contexts/PaymentContext';
import StripeCheckout from './StripeCheckout';
import PartnerPlanWall from './PartnerPlanWall';
import {
  AGENCY_EXTRA_SEAT_MONTHLY,
  TRAVELER_CREDIT_PACKS,
  travelerCreditPackAmount
} from '../config/plans';

const PartnerCapacityDialog = ({
  open,
  onClose,
  onSuccess,
  limitError
}) => {
  const { t } = useTranslation();
  const { createCheckoutSession, loading, error, clearError } = usePayment();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [planWallOpen, setPlanWallOpen] = useState(false);

  const code = limitError?.code;
  const entitlements = limitError?.entitlements;
  const recommendation = limitError?.recommendation;
  const checkoutType = limitError?.checkoutType;
  const requiresUpgrade = Boolean(limitError?.requiresUpgrade);
  const extraSeat = checkoutType === 'partner_extra_seat' || Boolean(entitlements?.limits?.extraSeatCents);
  const showCredits = code === 'partner_traveler_limit' && !requiresUpgrade && entitlements?.tier !== 'free';
  const showAiLimit = code === 'partner_ai_limit';

  const handleClose = () => {
    setCheckoutOpen(false);
    setCheckoutData(null);
    setSelectedTier(null);
    setPlanWallOpen(false);
    clearError();
    onClose?.();
  };

  const startCheckout = async (tierType, metadata = {}) => {
    try {
      clearError();
      setSelectedTier(tierType);
      const sessionData = await createCheckoutSession({
        tripId: null,
        tierType,
        usePartnerAuth: true,
        metadata
      });

      if (sessionData?.success && !sessionData.clientSecret && !sessionData.url) {
        onSuccess?.(sessionData);
        handleClose();
        return;
      }

      if (sessionData?.clientSecret) {
        setCheckoutData(sessionData);
        setCheckoutOpen(true);
      }
    } catch (err) {
      console.error('Partner capacity checkout failed:', err);
    }
  };

  const handlePaymentSuccess = (data) => {
    setCheckoutOpen(false);
    setCheckoutData(null);
    onSuccess?.(data);
    handleClose();
  };

  if (requiresUpgrade || showAiLimit || (code === 'partner_team_limit' && !extraSeat && !showCredits)) {
    return (
      <PartnerPlanWall
        open={open}
        onClose={handleClose}
        onSuccess={onSuccess}
        entitlements={entitlements}
        reason="limit"
      />
    );
  }

  return (
    <>
      <Dialog open={open && !planWallOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {code === 'partner_ai_limit'
            ? t('partnerPlans.capacity.aiTitle')
            : code === 'partner_team_limit'
            ? t('partnerPlans.capacity.seatTitle')
            : t('partnerPlans.capacity.travelerTitle')}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {code === 'partner_ai_limit'
              ? t('partnerPlans.capacity.aiBody', {
                used: limitError?.used ?? entitlements?.usage?.aiRequests ?? 0,
                limit: limitError?.limit ?? entitlements?.limits?.aiAllowance ?? 0
              })
              : code === 'partner_team_limit'
              ? t('partnerPlans.capacity.seatBody')
              : t('partnerPlans.capacity.travelerBody')}
          </Typography>

          {recommendation?.action === 'upgrade' && recommendation.message && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="partner-capacity-recommendation">
              {recommendation.message}
            </Alert>
          )}

          {showCredits && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {TRAVELER_CREDIT_PACKS.map((pack) => {
                const price = travelerCreditPackAmount(entitlements?.tier, pack);
                if (price == null) return null;
                return (
                  <Button
                    key={pack}
                    variant="outlined"
                    disabled={loading}
                    data-testid={`traveler-credit-pack-${pack}`}
                    onClick={() => startCheckout('partner_traveler_credits', { packSize: pack, pack_size: pack })}
                  >
                    {t('partnerPlans.capacity.creditsPack', { count: pack, price })}
                  </Button>
                );
              })}
            </Box>
          )}

          {code === 'partner_team_limit' && extraSeat && (
            <Button
              variant="contained"
              disabled={loading}
              data-testid="buy-extra-seat"
              onClick={() => startCheckout('partner_extra_seat', { quantity: 1 })}
              sx={{ mb: 2 }}
            >
              {loading && selectedTier === 'partner_extra_seat'
                ? <CircularProgress size={18} />
                : t('partnerPlans.capacity.buySeat', { price: AGENCY_EXTRA_SEAT_MONTHLY })}
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <Button onClick={() => setPlanWallOpen(true)}>
            {t('partnerPlans.upgrade')}
          </Button>
        </DialogActions>
      </Dialog>

      <PartnerPlanWall
        open={planWallOpen}
        onClose={() => setPlanWallOpen(false)}
        onSuccess={onSuccess}
        entitlements={entitlements}
        reason="limit"
      />

      <StripeCheckout
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        checkoutData={checkoutData}
        onSuccess={handlePaymentSuccess}
        onError={() => setCheckoutOpen(false)}
        upgradeType={selectedTier}
      />
    </>
  );
};

export default PartnerCapacityDialog;
